import React, { useEffect, useState, useRef } from 'react'
import axios from 'axios'
import ClassicCenteredTrainStatusComponent from '../ClassicCenteredTrainStatusComponent/ClassicCenteredTrainStatusComponent.js'
import ClassicLeftBoundTrainStatusComponent from '../ClassicLeftBoundTrainStatusComponent/ClassicLeftBoundTrainStatusComponent.js'
import ClassicBusRow from '../ClassicBusRow/ClassicBusRow.js'
import { themes } from '../../enums/enums.js'
import './view-scroller.css'

export default function ViewScroller({ listOfStationsToDisplay, selectedTheme }) {
  const [displayedData, setDisplayedData] = useState(null)
  const [busBannerTick, setBusBannerTick] = useState(0)
  const [userIdUi, setUserIdUi] = useState('')
  const counterRef = useRef(0) 
  const firstLoad = useRef(true)
  const prefetchedRef = useRef(null)
  const [viewContainerHeight, setViewContainerHeight] = useState(0);
  const [viewContainerDiv, setViewContainerDiv] = useState(null);
  const scrollContainerRef = useRef(null)
  const scrollAnimationRef = useRef(null)
  const lastTimeRef = useRef(null)
  const accumulatorRef = useRef(0)

  // FIXED: Wrapped in useCallback to prevent recreating on every render
  const fetchBusAndTrainData = useRef(async () => {
    try {
      const userId = localStorage.getItem('UserId')
      if (!userId) {
        console.error('UserId not found in localStorage')
        return
      }

      const response = await axios.get(
        process.env.REACT_APP_ENVIRONMENT + '/api/trains',
        { params: { userId } }
      )

      const resultList = response.data?.bussesAndTrainsList || []
      prefetchedRef.current = resultList
      console.log('Prefetched data stored (no rerender)')
    } catch (error) {
      console.error('Failed to fetch train information:', error)
      prefetchedRef.current = []
    }
  }).current // IMPORTANT: .current makes this stable across renders

  // Initial load
  useEffect(() => {
    async function init() {
      const userId = localStorage.getItem('UserId')
      if (!userId) return setDisplayedData([])

      try {
        const response = await axios.get(
          process.env.REACT_APP_ENVIRONMENT + '/api/trains',
          { params: { userId } }
        )
        const resultList = response.data?.bussesAndTrainsList || []
        setDisplayedData(resultList)
        prefetchedRef.current = resultList
        setUserIdUi('')
      } catch (error) {
        console.error('Initial data fetch failed:', error)
        setDisplayedData([])
      }
    }
    init()
  }, []) // Empty deps - runs once ✓

  useEffect(() => {
    if (!viewContainerDiv) return;

    const observer = new ResizeObserver(() => {
      setViewContainerHeight(viewContainerDiv.getBoundingClientRect().height);
      
      // When container resizes, this runs
      console.log('Container resized, height is now:', viewContainerDiv.getBoundingClientRect().height);
      if (viewContainerDiv.getBoundingClientRect().height > window.innerHeight) {
        console.log('Bigger!')
      }
    });

    observer.observe(viewContainerDiv);
    return () => observer.disconnect();
  }, [viewContainerDiv]);

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const checkAndStartScroll = () => {
        const isOverflowing = container.scrollHeight / 2 > container.clientHeight
        
        if (!isOverflowing) {
            // Content fits — no scroll needed
            cancelAnimationFrame(scrollAnimationRef.current)
            container.scrollTop = 0
            return
        }

        const SCROLL_SPEED = 20 // px per second, regardless of refresh rate

        const step = (timestamp) => {
            if (!lastTimeRef.current) lastTimeRef.current = timestamp
            const elapsed = timestamp - lastTimeRef.current
            lastTimeRef.current = timestamp

            accumulatorRef.current += SCROLL_SPEED * (elapsed / 1000)

            // Only scroll when we've accumulated at least 1px
            if (accumulatorRef.current >= 1) {
                const pixels = Math.floor(accumulatorRef.current)
                container.scrollTop += pixels
                accumulatorRef.current -= pixels // keep the leftover fraction
            }

            const halfwayPoint = container.scrollHeight / 2
            if (container.scrollTop >= halfwayPoint) {
                container.scrollTop -= halfwayPoint
            }

            scrollAnimationRef.current = requestAnimationFrame(step)
        }
        scrollAnimationRef.current = requestAnimationFrame(step)
    }

    // Re-evaluate whenever content or container size changes
    const observer = new ResizeObserver(checkAndStartScroll)
    observer.observe(container)
    checkAndStartScroll()

    return () => {
        observer.disconnect()
        cancelAnimationFrame(scrollAnimationRef.current)
    }
  }, [displayedData]) // re-run when data changes
  
  // FIXED: Interval manager with proper cleanup
  useEffect(() => {
    const tick = setInterval(() => {
      counterRef.current++

      // Every 6th tick (~24s): prefetch new data
      if (counterRef.current >= 6) {
        fetchBusAndTrainData()
        counterRef.current = 0
        console.log('Request made (no rerender)')
      }
      // Every 4th tick (~16s): swap prefetched → displayed
      else if (counterRef.current === 4) {
        if (prefetchedRef.current) {
          setDisplayedData(prefetchedRef.current)
          console.log('Swap → new data rendered')
        } else {
          console.warn('Skipped swap — no prefetched data yet')
        }
      }

      // Every tick: banner animation
      setBusBannerTick(prev => prev + 1) 
    }, 4000)

    return () => {
      clearInterval(tick)
      console.log('Interval cleaned up') // ADDED: Verify cleanup
    }
  }, [fetchBusAndTrainData]) // FIXED: Added fetchBusAndTrainData to deps

  // RENDERERS - FIXED: Memoized with useCallback
  const renderBusBanner = useRef((busRow, id) => {
    return (
      <ClassicBusRow
        key={id}
        busses={busRow}
        busBannerTick={busBannerTick}
        bannerId={id}
      />
    )
  }).current

  const renderTrainBanner = useRef((singleTrainBanner, id) => {
    const trainData = buildTrainData(singleTrainBanner)
    return selectedTheme === themes.ClassicCentered ? (
      <ClassicCenteredTrainStatusComponent trainData={trainData} bannerId={id} key={id} />
    ) : (
      <ClassicLeftBoundTrainStatusComponent trainData={trainData} bannerId={id} key={id} />
    )
  }).current

  function buildTrainData(train) {
    const t = train || {}
    const dir1 = t.trainDirectionOneInformation?.trainArrivalDetails || []
    const dir5 = t.trainDirectionFiveInformation?.trainArrivalDetails || []

    return {
      stationId: t.stationId || '',
      stationName: t.stationName || '',
      trainColorDisplay: t.trainColorDisplay || '',
      trainColorHexValue: t.trainColorHexValue || '',
      directionOneDestinationName: t.trainDirectionOneInformation?.destinationName || '',
      directionFiveDestinationName: t.trainDirectionFiveInformation?.destinationName || '',
      directionOneTimeOne: dir1[0]?.isApproaching === '1' ? 'Due' : dir1[0]?.arrivalTime || 'No inbound train',
      directionOneTimeTwo: dir1[1]?.arrivalTime || '',
      directionOneTimeThree: dir1[2]?.arrivalTime || '',
      directionFiveTimeOne: dir5[0]?.isApproaching === '1' ? 'Due' : dir5[0]?.arrivalTime || 'No inbound train',
      directionFiveTimeTwo: dir5[1]?.arrivalTime || '',
      directionFiveTimeThree: dir5[2]?.arrivalTime || '',
    }
  }

  /*return (
    <div ref={setViewContainerDiv} className="view-container" id="view-container">
      <div className="view-container-inner">
        {displayedData
          ? displayedData.map((item, idx) =>
              item.isBus
                ? renderBusBanner(item, idx + 1)
                : renderTrainBanner(item.train, idx + 1)
            )
          : <div>Loading data...</div>}
      </div>
    </div>
  )*/

    return (
  <div
    ref={scrollContainerRef}
    className="view-container"
    id="view-container"
    style={{ overflow: 'hidden' }} // hide scrollbar, we control scrollTop manually
  >
    <div className='view-container-inner'>
        {displayedData ? (
        <>
            {/* First copy */}
            {displayedData.map((item, idx) =>
            item.isBus
                ? renderBusBanner(item, `a-${idx + 1}`)
                : renderTrainBanner(item.train, `a-${idx + 1}`)
            )}
            {/* Duplicate copy for seamless loop */}
            {displayedData.map((item, idx) =>
            item.isBus
                ? renderBusBanner(item, `b-${idx + 1}`)
                : renderTrainBanner(item.train, `b-${idx + 1}`)
            )}
        </>
        ) : (
        <div>Loading data...</div>
        )}
    </div>
  </div>
    )
}